/**
 * My Watches Client API
 *
 * Client-side functions for the watch registration flow. These call our BFF
 * routes (`/api/account/watches/*`), which proxy to the My Account App.
 * Mirrors lib/wishlist/client.ts: self-catching, 401 is an expected guest
 * outcome, never throws to the caller.
 */

'use client';

import type { HydratedWatch } from '@/lib/myWatchesData/orchestrator';
import { devError, devLog } from '@/lib/utils/devLog';

import type { Watch } from './types';

/** Warranty lookup outcome: the backend status code plus the watch record. */
export interface WarrantyLookupResult {
  code: number;
  data?: Watch;
}

export type RegisterWatchResult =
  | { status: 'success'; watch?: Watch }
  | { status: 'unauthenticated' }
  | { status: 'error'; code?: number };

interface WarrantyLookupParams {
  sn: string;
  sec?: string;
  model?: string;
  locale?: string;
}

/**
 * Look up a serial's warranty/registration status.
 *
 * Returns null only on transport failure; backend outcomes (including
 * not-found codes) come back as `{ code, data }`.
 */
export async function fetchWatchWarranty(
  params: WarrantyLookupParams
): Promise<WarrantyLookupResult | null> {
  try {
    const query = new URLSearchParams({ sn: params.sn });
    if (params.sec) query.set('sec', params.sec);
    if (params.model) query.set('model', params.model);
    if (params.locale) query.set('locale', params.locale);

    const response = await fetch(`/api/account/watches/warranty?${query.toString()}`);

    if (!response.ok) {
      devError('[MyWatches Client] Warranty lookup failed:', response.status);
      return null;
    }

    const json = await response.json();

    if (!json.success || typeof json.data?.code !== 'number') {
      devError('[MyWatches Client] Warranty lookup returned no code');
      return null;
    }

    devLog('[MyWatches Client] Warranty:', json.data.code);

    return { code: json.data.code, data: json.data.data };
  } catch (error) {
    devError('[MyWatches Client] Warranty lookup error:', error);
    return null;
  }
}

interface RegisterWatchParams {
  sn: string;
  sku: string;
  sec?: string;
  locale: string;
}

/**
 * Register a watch to the authenticated user.
 *
 * A 401 means the visitor is a guest — callers route them to login/signup
 * with the pending registration stored.
 */
export async function registerWatch(params: RegisterWatchParams): Promise<RegisterWatchResult> {
  try {
    devLog('[MyWatches Client] Registering:', params.sn);

    const response = await fetch('/api/account/watches/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (response.status === 401) {
      devLog('[MyWatches Client] Not authenticated');
      return { status: 'unauthenticated' };
    }

    const json = await response.json().catch(() => null);

    if (!response.ok || !json?.success) {
      const code = typeof json?.data?.code === 'number' ? json.data.code : undefined;
      devError('[MyWatches Client] Register failed:', response.status, code);
      return { status: 'error', code };
    }

    const code = json.data?.code;

    if (code !== 200) {
      devLog('[MyWatches Client] Register rejected:', code);
      return { status: 'error', code };
    }

    devLog('[MyWatches Client] Registered successfully');
    return { status: 'success', watch: json.data?.data };
  } catch (error) {
    devError('[MyWatches Client] Register error:', error);
    return { status: 'error' };
  }
}

interface RegisterWatchUnconfirmedParams {
  email: string;
  sn: string;
  sku: string;
  sec?: string;
  locale: string;
}

/**
 * Register a watch for a just-signed-up, unconfirmed account.
 *
 * @returns true when the backend accepted the registration (envelope 200)
 */
export type FetchMyWatchesResult =
  | { status: 'success'; watches: Watch[] }
  | { status: 'unauthenticated' }
  | { status: 'error' };

export type FetchHydratedMyWatchesResult =
  | { status: 'success'; watches: HydratedWatch[] }
  | { status: 'unauthenticated' }
  | { status: 'error' };

/**
 * Fetch the authenticated user's registered watches with Algolia display data.
 */
export async function fetchHydratedMyWatches(
  locale: string
): Promise<FetchHydratedMyWatchesResult> {
  try {
    const query = new URLSearchParams({ locale });
    const response = await fetch(`/api/account/watches/hydrated?${query.toString()}`);

    if (response.status === 401) {
      return { status: 'unauthenticated' };
    }

    const json = await response.json().catch(() => null);
    if (!response.ok || !json?.success || !Array.isArray(json.data?.watches)) {
      devError('[MyWatches Client] Hydrated list fetch failed:', response.status);
      return { status: 'error' };
    }

    return { status: 'success', watches: json.data.watches as HydratedWatch[] };
  } catch (error) {
    devError('[MyWatches Client] Hydrated list fetch error:', error);
    return { status: 'error' };
  }
}

/**
 * Fetch the authenticated user's registered watches.
 */
export async function fetchMyWatches(): Promise<FetchMyWatchesResult> {
  try {
    const response = await fetch('/api/account/watches');

    if (response.status === 401) {
      return { status: 'unauthenticated' };
    }

    const json = await response.json().catch(() => null);
    if (!response.ok || !json?.success || !Array.isArray(json.data?.watches)) {
      devError('[MyWatches Client] List fetch failed:', response.status);
      return { status: 'error' };
    }

    return { status: 'success', watches: json.data.watches as Watch[] };
  } catch (error) {
    devError('[MyWatches Client] List fetch error:', error);
    return { status: 'error' };
  }
}

export async function registerWatchUnconfirmed(
  params: RegisterWatchUnconfirmedParams
): Promise<boolean> {
  try {
    devLog('[MyWatches Client] Registering for unconfirmed account:', params.sn);

    const response = await fetch('/api/account/watches/register-unconfirmed', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    const json = await response.json().catch(() => null);

    const accepted = Boolean(response.ok && json?.success && json.data?.code === 200);

    devLog('[MyWatches Client] Unconfirmed register result:', json?.data?.code);

    return accepted;
  } catch (error) {
    devError('[MyWatches Client] Unconfirmed register error:', error);
    return false;
  }
}
